import { useContext, useEffect, useMemo, useState } from 'react';
import { shallowEqual } from 'react-redux';
import Cookies from 'js-cookie';

import { DetailedIconForSaleSign, DetailedIconHomeTour, IconExternal } from '@zillow/constellation';
import { Box, Button, Heading, Paragraph, Text } from '@zillow/constellation-10';

import TenantPlacementCSEvents from '../../../../../model/ClickstreamEvent/TenantPlacementCSEvents';
import { RMWContext } from '../../../../../providers/RMWContext';

import { useSelector } from '../../../../../selectors/useSelector';

import { TENANT_PLACEMENT_COOKIES } from '../../../../../constants/CookieConstants';
import { TENANT_PLACEMENT_ENTRY_POINTS } from '../../../../TenantPlacement/TenantPlacement';

import { StyledUpsell } from './StyledComponents';

const TenantPlacementUpsell = ({
  isInReactivationFlow,
  onPostListingClick,
  propertyId,
  streetAddress,
}: {
  isInReactivationFlow: boolean;
  onPostListingClick: () => void;
  streetAddress?: string;
  propertyId?: string;
}) => {
  const {
    properties: { currentProperty },
    user,
    isProd,
  } = useSelector(
    (state) => ({
      properties: state.properties,
      user: state.user,
      isProd: state.app.isRunningInProd,
    }),
    shallowEqual,
  );

  const [isPostListingDisabled, setIsPostListingDisabled] = useState(false);

  const { paths } = useContext(RMWContext);

  // query params for "Learn More" selection
  const tenantPlacementPageUrl = useMemo(() => {
    const url = new URL(paths.tenantPlacement, isProd ? 'https://www.zillow.com/' : 'https://www.qa.zillow.net/');
    streetAddress && url.searchParams.set('listingAddress', streetAddress);
    propertyId && url.searchParams.set('lotId', propertyId);
    currentProperty.listingAlias && url.searchParams.set('listingAlias', currentProperty.listingAlias);
    url.searchParams.set(
      'state',
      streetAddress ? streetAddress.split(', ').pop()?.split(' ')[0] : currentProperty.state,
    );
    url.searchParams.set(
      'from',
      isInReactivationFlow ? TENANT_PLACEMENT_ENTRY_POINTS.PREVIOUS_LISTING : TENANT_PLACEMENT_ENTRY_POINTS.NEW_LISTING,
    );
    return url;
  }, [streetAddress, propertyId, currentProperty, isInReactivationFlow]);

  useEffect(() => {
    if (isInReactivationFlow) {
      TenantPlacementCSEvents.TenantPlacementReactivateListingUpsellModal({ currentProperty, user });
    } else {
      TenantPlacementCSEvents.TenantPlacementNewListingUpsellModal({ user });
    }
  }, []);

  const handleLearnMoreClickStreamEvent = () => {
    if (isInReactivationFlow) {
      TenantPlacementCSEvents.TenantPlacementReactivationLearnMoreButton({ currentProperty, user });
    } else {
      TenantPlacementCSEvents.TenantPlacementNewListingLearnMoreButton({ currentProperty, user });
    }
  };

  const handlePostListingClickStreamEvent = () => {
    if (isInReactivationFlow) {
      TenantPlacementCSEvents.TenantPlacementReactivateListingButton({ currentProperty, user });
    } else {
      TenantPlacementCSEvents.TenantPlacementPostNewListingButton({ currentProperty, user });
    }
  };

  const handleLearnMoreClick = () => {
    handleLearnMoreClickStreamEvent();
  };

  const handlePostListingClick = () => {
    Cookies.set(
      isInReactivationFlow
        ? TENANT_PLACEMENT_COOKIES.HAS_CLICKED_DASHBOARD_POST
        : TENANT_PLACEMENT_COOKIES.HAS_CLICKED_ADD_PROPERTY_MODAL_POST,
      'true',
      {
        expires: TENANT_PLACEMENT_COOKIES.EXPIRATION,
      },
    );
    setIsPostListingDisabled(true);
    onPostListingClick();
    handlePostListingClickStreamEvent();
  };
  return (
    <>
      <Box css={{ marginInline: '600' }}>
        <Heading level={4} css={{ margin: '100' }}>
          How do you want to find your tenant?
        </Heading>
        <Paragraph css={{ marginInline: '200', marginTop: '400' }}>
          You can use Zillow Rental Manager to list a property and find your tenant for free. Or take advantage of
          Zillow&apos;s partners and try their tenant placement service - let them do the heavy lifting to find a
          qualified tenant for your property.
        </Paragraph>
      </Box>
      <Box css={{ display: 'flex', flexDirection: { base: 'column', lg: 'row' }, margin: '600', gap: '600' }}>
        <StyledUpsell
          body={
            <Box css={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
              <DetailedIconForSaleSign marginTop="sm" />
              <Heading level={6} css={{ marginBottom: '400' }}>
                Do it yourself
              </Heading>
              <Text className="fs-unmask">
                {isInReactivationFlow
                  ? 'List your property for rent. It’s free to post and takes just a few minutes!'
                  : 'List your property, receive applications, and screen tenants for free.'}
              </Text>
              <br />
              <Button
                tone="brand"
                emphasis={isInReactivationFlow ? 'filled' : 'outlined'}
                onClick={handlePostListingClick}
                disabled={isPostListingDisabled}
                data-testid="TenantPlacementUpsell-PostListingButton"
                data-eventid={
                  isInReactivationFlow
                    ? TenantPlacementCSEvents.TenantPlacementReactivateListingButton.data_event_id
                    : TenantPlacementCSEvents.TenantPlacementPostNewListingButton.data_event_id
                }
                css={{
                  marginTop: '400',
                  maxWidth: 'max-content',
                  xl: {
                    position: 'absolute',
                    left: '400',
                    bottom: '400',
                    right: '400',
                  },
                }}
              >
                Post listing
              </Button>
            </Box>
          }
          direction="column"
          cardElevation={0}
          closeable={false}
        />
        <StyledUpsell
          body={
            <>
              <DetailedIconHomeTour marginTop="sm" />
              <Heading level={6} css={{ marginBottom: '400' }}>
                Use partner&apos;s tenant placement service
              </Heading>
              <Text>
                Let the pros take care of listing, showing, and screening applicants. You only pay when a lease is
                signed.
              </Text>
              <br />
              <Button
                tone="brand"
                emphasis="outlined"
                icon={<IconExternal />}
                iconPosition="end"
                asChild
                onClick={handleLearnMoreClick}
                data-eventid={
                  isInReactivationFlow
                    ? TenantPlacementCSEvents.TenantPlacementReactivationLearnMoreButton.data_event_id
                    : TenantPlacementCSEvents.TenantPlacementNewListingLearnMoreButton.data_event_id
                }
                css={{ marginTop: '400' }}
              >
                <a href={tenantPlacementPageUrl.toString()} target="_blank" rel="noopener noreferrer">
                  Learn more
                </a>
              </Button>
            </>
          }
          direction="column"
          cardElevation={0}
          closeable={false}
        />
      </Box>
    </>
  );
};

export default TenantPlacementUpsell;
